import React, { useState } from 'react';
import {
  Container, Box, Typography, Grid, Card, TextField, Button, MenuItem, Chip, Avatar
} from '@mui/material';
import {
  CheckCircle, PlayArrow, Shield, Speed, TrendingUp, Groups,
  ArrowForward, AccessTime, Verified, EmojiEvents, Star
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import { websiteAPI } from '../../services/api';
import WebsiteLayout from '../../components/Website/WebsiteLayout';
import SEO from '../../components/SEO/SEO';
import { getPageSEO } from '../../config/seo';
import { useRegion } from '../../context/RegionContext';
import PhoneInput from '../../components/shared/PhoneInput';
import HeroSection from '../../components/Website/HeroSection';
import TestimonialCarousel from '../../components/Website/TestimonialCarousel';
import { useTranslation } from 'react-i18next';

const FOREST = '#163300';
const GREEN  = '#9FE870';

const STATS = [
  { value: '2,000+', label: 'Businesses Onboarded' },
  { value: '98%',    label: 'Customer Satisfaction' },
  { value: '< 24h',  label: 'Avg. Response Time' },
  { value: '4.9★',   label: 'Average Rating' },
];

const OUTCOMES = [
  { icon: <Speed />,      title: 'Cut Close Time by 60%',        desc: 'Automate month-end with smart reconciliation and posting engine.' },
  { icon: <TrendingUp />, title: 'Real-Time P&L in One Click',   desc: 'Live dashboards replace spreadsheet chaos instantly.' },
  { icon: <Shield />,     title: 'Audit-Ready at All Times',     desc: 'Every transaction logged, versioned, and traceable.' },
  { icon: <Groups />,     title: 'Multi-Company, One Login',     desc: 'Manage unlimited entities from a single workspace.' },
];

const TESTIMONIALS = [
  { name: 'Sarah K.',  role: 'CFO',               company: 'Retail Chain (UK)',          avatar: 'S', rating: 5, text: 'FluxERP replaced three separate tools. Our team saved 12 hours a week on reporting alone — the ROI was visible within the first month.' },
  { name: 'Ahmed R.', role: 'Finance Director',   company: 'Manufacturing Group (UAE)',   avatar: 'A', rating: 5, text: 'The demo showed us exactly what we needed. We were live in under two weeks with full data migration handled by their team.' },
  { name: 'Priya M.', role: 'Operations Manager', company: 'Professional Services (IN)',  avatar: 'P', rating: 5, text: 'Best ERP decision we ever made. The onboarding team was exceptional and the multi-company setup saved us hours every month-end.' },
  { name: 'James T.', role: 'Managing Director',  company: 'Distribution Co. (QA)',       avatar: 'J', rating: 5, text: 'We evaluated five ERPs. FluxERP was the only one that handled our multi-warehouse, multi-currency setup out of the box.' },
  { name: 'Layla H.', role: 'Head of Finance',    company: 'E-commerce Brand (PK)',       avatar: 'L', rating: 5, text: 'The real-time P&L and automated reconciliation alone justified the cost. Our auditors were impressed by the audit trail.' },
];

const COMPANY_SIZES = ['1–10 employees', '11–50 employees', '51–200 employees', '201–500 employees', '500+ employees'];
const USE_CASES = ['Accounting & Finance', 'Inventory Management', 'CRM & Sales', 'Payroll & HR', 'Multi-Company Management', 'Complete ERP Solution'];

const RequestDemoPage = () => {
  const { t } = useTranslation();
  const seo = getPageSEO('demo');
  const { phonePrefix } = useRegion();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref') || localStorage.getItem('partnerReferralCode') || '';
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', company: '', companySize: '',
    countryCode: phonePrefix, phone: '', useCase: '',
    preferredDate: '', preferredTime: ''
  });

  const set = (field) => (e) => setFormData(p => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await websiteAPI.submitDemoRequest({
        fullName: formData.name,
        email: formData.email,
        companyName: formData.company,
        companySize: formData.companySize,
        phone: `${formData.countryCode} ${formData.phone}`,
        preferredDate: formData.preferredDate,
        preferredTime: formData.preferredTime,
        useCase: formData.useCase,
        notes: `Company Size: ${formData.companySize}\nUse Case: ${formData.useCase}`,
        referralCode: referralCode || undefined
      });
      setSubmitted(true);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to submit — please try again.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <WebsiteLayout>
      <SEO {...seo} />

      {/* ── HERO ── */}
      <HeroSection chip={t('website.demo.heroChip')} minHeight="520px">
        <Box sx={{ textAlign: 'center', maxWidth: 820, mx: 'auto' }}>
          <Typography variant="h1" sx={{ fontSize: { xs: 36, md: 58 }, fontWeight: 800, mb: 2, color: FOREST, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
            {t('website.demo.heroTitle')}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.85, lineHeight: 1.7, color: FOREST, mb: 4, maxWidth: 640, mx: 'auto', fontWeight: 400 }}>
            {t('website.demo.heroSubtitle')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 2 }}>
            {t('website.demo.benefits', { returnObjects: true })?.map(t => (
              <Chip key={t} icon={<CheckCircle sx={{ fontSize: '16px !important', color: `${FOREST} !important` }} />}
                label={t} sx={{ bgcolor: 'rgba(22,51,0,0.12)', color: FOREST, fontWeight: 700, fontSize: '0.8rem', border: '1px solid rgba(22,51,0,0.2)' }} />
            ))}
          </Box>
        </Box>
      </HeroSection>

      {/* ── STATS BAR ── */}
      <Box sx={{ bgcolor: FOREST, py: 3 }}>
        <Container maxWidth="lg">
          <Grid container spacing={2} justifyContent="center">
            {(t('website.demo.stats', { returnObjects: true }) || []).map(s => (
              <Grid item xs={6} md={3} key={s.label} sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 800, color: GREEN, lineHeight: 1 }}>{s.value}</Typography>
                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', mt: 0.5, fontWeight: 500 }}>{s.label}</Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── MAIN CONTENT: FORM + SIDEBAR ── */}
      <Box sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'background.default' : '#f7faf4', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} alignItems="flex-start">

            {/* FORM */}
            <Grid item xs={12} md={7}>
              {submitted ? (
                <Card sx={{ p: 6, textAlign: 'center', border: `2px solid ${GREEN}`, borderRadius: 3 }}>
                  <EmojiEvents sx={{ fontSize: 64, color: GREEN, mb: 2 }} />
                  <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: FOREST }}>{t('website.demo.form.successTitle')}</Typography>
                  <Typography color="text.secondary" sx={{ mb: 3 }}>
                    {t('website.demo.form.successSubtitle')}
                  </Typography>
                  <Chip label={t('website.demo.form.successChip')} sx={{ bgcolor: 'rgba(159,232,112,0.2)', color: FOREST, fontWeight: 700, fontSize: '0.9rem', px: 2, py: 2.5 }} />
                </Card>
              ) : (
                <Card sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, border: '1px solid', borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(22,51,0,0.1)', boxShadow: '0 8px 40px rgba(22,51,0,0.08)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                    <PlayArrow sx={{ color: GREEN, bgcolor: FOREST, borderRadius: '50%', p: 0.5, fontSize: 28 }} />
                    <Typography variant="h4" sx={{ fontWeight: 800, color: FOREST }}>{t('website.demo.form.title')}</Typography>
                  </Box>
                  <Typography color="text.secondary" sx={{ mb: 4, fontSize: '0.95rem' }}>
                    {t('website.demo.form.subtitle')}
                  </Typography>

                  <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label={t('website.demo.form.fullName')} required value={formData.name} onChange={set('name')} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label={t('website.demo.form.email')} type="email" required value={formData.email} onChange={set('email')} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label={t('website.demo.form.company')} required value={formData.company} onChange={set('company')} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label={t('website.demo.form.companySize')} select required value={formData.companySize} onChange={set('companySize')}>
                          {COMPANY_SIZES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <PhoneInput
                          countryCode={formData.countryCode} phoneNumber={formData.phone}
                          onCountryCodeChange={c => setFormData(p => ({ ...p, countryCode: c }))}
                          onPhoneNumberChange={v => setFormData(p => ({ ...p, phone: v }))}
                          label={t('website.demo.form.phone')} required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label={t('website.demo.form.primaryInterest')} select required value={formData.useCase} onChange={set('useCase')}>
                          {USE_CASES.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label={t('website.demo.form.preferredDate')} type="date" InputLabelProps={{ shrink: true }} value={formData.preferredDate} onChange={set('preferredDate')} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label={t('website.demo.form.preferredTime')} type="time" InputLabelProps={{ shrink: true }} value={formData.preferredTime} onChange={set('preferredTime')} />
                      </Grid>
                      <Grid item xs={12}>
                        <Button type="submit" variant="contained" fullWidth disabled={loading}
                          endIcon={<ArrowForward />}
                          sx={{
                            py: 2, borderRadius: 2, fontSize: 17, fontWeight: 800,
                            bgcolor: FOREST, color: GREEN,
                            boxShadow: `0 8px 24px rgba(22,51,0,0.25)`,
                            '&:hover': { bgcolor: '#0b1a00', transform: 'translateY(-2px)', boxShadow: `0 12px 32px rgba(22,51,0,0.35)` },
                            transition: 'all 0.25s'
                          }}>
                          {loading ? t('website.demo.form.submitting') : t('website.demo.form.submit')}
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}>
                          {t('website.demo.form.privacy')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Card>
              )}
            </Grid>

            {/* SIDEBAR */}
            <Grid item xs={12} md={5}>
              <Box sx={{ position: 'sticky', top: 100, display: 'flex', flexDirection: 'column', gap: 3 }}>

                {/* What you get */}
                <Card sx={{ p: 3.5, bgcolor: FOREST, borderRadius: 3, border: 'none' }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: GREEN, mb: 2.5 }}>{t('website.demo.sidebar.whatHappens')}</Typography>
                  {(() => {
                    const icons = [
                      <AccessTime sx={{ fontSize: 18 }} />,
                      <Verified sx={{ fontSize: 18 }} />,
                      <Star sx={{ fontSize: 18 }} />,
                      <Shield sx={{ fontSize: 18 }} />
                    ];
                    return (t('website.demo.sidebar.items', { returnObjects: true }) || []).map((item, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 2, mb: i < 3 ? 2.5 : 0 }}>
                        <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: 'rgba(159,232,112,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: GREEN, flexShrink: 0 }}>
                          {icons[i]}
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 700, color: 'white', fontSize: '0.9rem', lineHeight: 1.3 }}>{item.title}</Typography>
                          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem', mt: 0.3 }}>{item.desc}</Typography>
                        </Box>
                      </Box>
                    ));
                  })()}
                </Card>

                {/* Urgency nudge */}
                <Card sx={{ p: 2.5, borderRadius: 3, border: `1.5px solid ${GREEN}`, bgcolor: 'rgba(159,232,112,0.06)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#4caf50', boxShadow: '0 0 0 3px rgba(76,175,80,0.25)', flexShrink: 0 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: FOREST }}>
                      {t('website.demo.sidebar.urgency')}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mt: 0.8, pl: 3 }}>
                    {t('website.demo.sidebar.urgencyDesc')}
                  </Typography>
                </Card>

                {/* Testimonial Carousel */}
                <TestimonialCarousel
                  testimonials={TESTIMONIALS}
                  interval={5000}
                  sx={{ px: 3 }}
                />

              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── OUTCOMES ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <Chip label={t('website.demo.outcomes.chip')} sx={{ bgcolor: 'rgba(159,232,112,0.15)', color: FOREST, fontWeight: 700, mb: 2 }} />
            <Typography variant="h2" sx={{ fontWeight: 800, fontSize: { xs: 30, md: 44 }, mb: 1.5 }}>
              {t('website.demo.outcomes.title')}
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 520, mx: 'auto', lineHeight: 1.7 }}>
              {t('website.demo.outcomes.subtitle')}
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {(() => {
              const icons = [<Speed />, <TrendingUp />, <Shield />, <Groups />];
              return (t('website.demo.outcomes.items', { returnObjects: true }) || []).map((o, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Card sx={{
                    p: 3.5, height: '100%', borderRadius: 3,
                    border: '1px solid', borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(22,51,0,0.08)',
                    transition: 'all 0.3s',
                    '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 16px 40px rgba(22,51,0,0.1)', borderColor: GREEN }
                  }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: FOREST, display: 'flex', alignItems: 'center', justifyContent: 'center', color: GREEN, mb: 2.5 }}>
                      {icons[i]}
                    </Box>
                    <Typography sx={{ fontWeight: 800, mb: 1, fontSize: '1rem' }}>{o.title}</Typography>
                    <Typography color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.6 }}>{o.desc}</Typography>
                  </Card>
                </Grid>
              ));
            })()}
          </Grid>
        </Container>
      </Box>

      {/* ── BOTTOM CTA ── */}
      <Box sx={{ bgcolor: FOREST, py: { xs: 8, md: 10 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h2" sx={{ fontWeight: 800, color: GREEN, fontSize: { xs: 28, md: 44 }, mb: 2 }}>
            {t('website.demo.bottomCta.title')}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.75)', mb: 5, fontSize: '1.05rem', lineHeight: 1.7 }}>
            {t('website.demo.bottomCta.subtitle')}
          </Typography>
          <Button variant="contained" size="large"
            href="#top" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            endIcon={<ArrowForward />}
            sx={{
              px: 6, py: 2, borderRadius: 2, fontWeight: 800, fontSize: 17,
              bgcolor: GREEN, color: FOREST,
              boxShadow: `0 8px 32px rgba(159,232,112,0.3)`,
              '&:hover': { bgcolor: '#b6f192', transform: 'translateY(-2px)' },
              transition: 'all 0.25s'
            }}>
            {t('website.demo.bottomCta.button')}
          </Button>
          <Typography sx={{ color: 'rgba(255,255,255,0.45)', mt: 2, fontSize: '0.8rem' }}>
            {t('website.demo.bottomCta.note')}
          </Typography>
        </Container>
      </Box>

    </WebsiteLayout>
  );
};

export default RequestDemoPage;
